import { Component, createMemo, JSX, Match, Switch } from 'solid-js';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { Navigate } from '@solidjs/router';
import { getConfig } from '../../config/config';

export interface DeveloperRouteGuardProps {
  readonly children: JSX.Element;
}

const TRIMBLE_EMAIL_DOMAIN = '@trimble.com';

/**
 * Guards developer-only routes based on environment and user email.
 * - In 'prod' environment: Route is not available, redirects to home.
 * - In 'rc' environment: Only allows users with @trimble.com email.
 * - In other environments (including 'localprod', which only runs on a
 *   developer machine): Allows all authenticated users.
 */
export const DeveloperRouteGuard: Component<DeveloperRouteGuardProps> = (
  props
) => {
  const auth = useAuth();
  const environment = getConfig().envName;

  const isProdEnvironment = (): boolean => environment === 'prod';

  const isRcEnvironment = (): boolean => environment === 'rc';

  const isTrimbleUser = createMemo((): boolean => {
    const email = auth().user?.email?.toLowerCase();
    return email?.endsWith(TRIMBLE_EMAIL_DOMAIN) ?? false;
  });

  const isAuthenticated = createMemo(
    (): boolean => auth().isAuthenticated === true
  );

  const hasAccess = createMemo((): boolean => {
    if (isProdEnvironment()) {
      return false;
    }

    if (isRcEnvironment()) {
      return isAuthenticated() && isTrimbleUser();
    }

    return isAuthenticated();
  });

  return (
    <Switch fallback={<Navigate href="/" />}>
      <Match when={hasAccess()}>{props.children}</Match>
    </Switch>
  );
};
